import React, { useState } from "react";
import { Button, Divider } from "../ui";
import { usePDF, Margin } from "react-to-pdf";
import { QRCodeSVG } from "qrcode.react";
import { formatDateBR } from "../../utils/dateUtils";
import {
  MapPin,
  Calendar,
  Package,
  Navigation,
  Users,
  Download,
  Eye,
  Printer,
  Share2,
  Copy,
  Check,
  Loader2,
} from "lucide-react";

function DetailInscricao({ detail }) {
  const { toPDF, targetRef } = usePDF({
    filename: "comprovante-corrida.pdf",
    page: {
      margin: Margin.SMALL,
    },
    overrides: {
      canvas: {
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc) => {
          // Remove restrições de altura e overflow dos containers do modal no clone
          // para que o html2canvas capture o comprovante inteiro mesmo com scroll ativo
          const scrollableContainers = clonedDoc.querySelectorAll(
            ".overflow-y-auto, .overflow-hidden"
          );
          scrollableContainers.forEach((el) => {
            el.style.overflow = "visible";
            el.style.maxHeight = "none";
          });
        },
      },
    },
  });
  const logo = require("../../statics/img/escudo-preto.png");
  const [showComprovante, setShowComprovante] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  if (!detail) return null;

  const toDate = (value) => {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDate = (value) => {
    return formatDateBR(value);
  };

  const formatTime = (value) => {
    if (!value) return "-";
    if (typeof value === "string") {
      const timeOnly = value.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
      if (timeOnly) {
        return `${timeOnly[1]}:${timeOnly[2]}`;
      }
    }
    const date = toDate(value);
    return date
      ? date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
      : "-";
  };

  const isConfirmado =
    detail.status &&
    (String(detail.status).toLowerCase().trim() === "inscrito" ||
      String(detail.status).toLowerCase().trim() === "confirmado" ||
      String(detail.status).toLowerCase().trim() === "concluída" ||
      String(detail.status).toLowerCase().trim() === "finalizada");

  const nomeAtleta = detail.nome_corredor || [detail.first_name, detail.last_name].filter(Boolean).join(" ") || "-";

  const getComprovanteText = () => {
    return [
      `🏁 COMPROVANTE DE INSCRIÇÃO - URBRACE`,
      `Prova: ${detail.nome_corrida || "-"}`,
      `Atleta: ${nomeAtleta}`,
      ...(detail.is_terceiro ? [`Tipo: Inscrição para Terceiro`, ...(detail.comprador_nome ? [`Comprado por: ${detail.comprador_nome}`] : [])] : []),
      `Nº de Peito: ${detail.num_corredor || "-"}`,
      `Categoria: ${detail.categoria || "-"} (${detail.faixa_etaria || "-"})`,
      ...(detail.tamanho_blusa ? [`Tamanho da Blusa: ${detail.tamanho_blusa}`] : []),
      `Percurso: ${detail.tamanho_percurso ? `${detail.tamanho_percurso} Km` : "-"}`,
      `Data do Evento: ${formatDate(detail.data_evento)}`,
      `Código de Check-in: ${detail.id_requisicao || detail.id || "-"}`,
      `Status: ${detail.status || "Confirmado"}`,
    ].join("\n");
  };

  const handleShare = async () => {
    const text = getComprovanteText();
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: `Comprovante - ${detail.nome_corrida || "Corrida"}`,
          text: text,
          url: window.location.href,
        });
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
        console.warn("Aviso ao compartilhar comprovante:", err);
      }
    }

    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
        return;
      } catch (e) {
        console.warn("Erro ao copiar para clipboard:", e);
      }
    }

    const encoded = encodeURIComponent(text);
    window.open(`https://api.whatsapp.com/send?text=${encoded}`, "_blank");
  };

  const handleCopyCode = async () => {
    const code = detail.id_requisicao || detail.id?.toString() || "";
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch (err) {
        console.warn("Erro ao copiar código:", err);
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      setPdfLoading(true);
      await toPDF();
    } catch (err) {
      console.error(
        "Falha ao gerar PDF, usando impressão nativa como contingência:",
        err
      );
      window.print();
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 text-left">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-xl sm:text-2xl font-black text-white m-0 tracking-tight">
          {detail.nome_corrida}
        </h2>
        <span className="px-3 py-1 rounded-full text-xs font-bold bg-urb-primary/10 text-urb-primary border border-urb-primary/30 w-fit">
          {detail.status}
        </span>
      </div>

      <Divider className="my-2" />

      {/* Info Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Atleta */}
        <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/60 flex flex-col gap-1 sm:col-span-2">
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-urb-primary" /> Atleta Corredor
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-white">{nomeAtleta}</span>
            {detail.is_terceiro ? (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">
                Terceiro
              </span>
            ) : (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                Titular
              </span>
            )}
          </div>
          {detail.is_terceiro && detail.comprador_nome && (
            <span className="text-[11px] text-slate-400">
              Inscrito por: <strong className="text-slate-300">{detail.comprador_nome}</strong>
            </span>
          )}
        </div>

        {/* Onde */}
        <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/60 flex flex-col gap-1">
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-urb-primary" /> Onde
          </span>
          <span className="text-xs sm:text-sm font-semibold text-white">
            {detail.cidade} - {detail.uf}
          </span>
        </div>

        {/* Quando */}
        <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/60 flex flex-col gap-1">
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-urb-primary" /> Quando
          </span>
          <span className="text-xs sm:text-sm font-semibold text-white">
            {formatDate(detail.data_evento)} às {formatTime(detail.hora)}
          </span>
        </div>

        {/* Entrega do Kit */}
        {detail.dt_entrega_kit && (
          <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/60 flex flex-col gap-1">
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5 text-urb-primary" /> Entrega do Kit
            </span>
            <span className="text-xs sm:text-sm font-semibold text-white">
              {formatDate(detail.dt_entrega_kit)}
            </span>
          </div>
        )}

        {/* Percurso */}
        <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/60 flex flex-col gap-1">
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
            <Navigation className="w-3.5 h-3.5 text-urb-primary" /> Percurso
          </span>
          <span className="text-xs sm:text-sm font-semibold text-white">
            {detail.tamanho_percurso} Km
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {/* Categoria */}
        <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/60 flex flex-col gap-1 sm:col-span-2">
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-urb-primary" /> Categoria
          </span>
          <span className="text-xs sm:text-sm font-semibold text-white">
            {detail.categoria} - {detail.faixa_etaria}
          </span>
        </div>

        {/* Blusa */}
        <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/60 flex flex-col gap-1">
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
            Tamanho da Blusa
          </span>
          <span className="text-xs sm:text-sm font-semibold text-white">
            {detail.tamanho_blusa || "-"}
          </span>
        </div>

        {/* Equipe */}
        <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/60 flex flex-col gap-1">
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
            Equipe / Patrocínio
          </span>
          <span className="text-xs sm:text-sm font-semibold text-white">
            {detail.equipe_patrocinio || "-"}
          </span>
        </div>
      </div>

      {isConfirmado && (
        <div className="flex flex-col items-center gap-4 mt-4">
          <div className="flex flex-col items-center justify-center w-full gap-2">
            {showComprovante ? (
              <div className="flex flex-wrap items-center justify-center gap-2.5 w-full no-print">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleDownloadPDF}
                  disabled={pdfLoading}
                  className="font-bold flex items-center gap-2"
                  title="Baixar comprovante em PDF"
                >
                  {pdfLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span>{pdfLoading ? "Gerando PDF..." : "Baixar PDF"}</span>
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handlePrint}
                  className="font-bold flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white border-slate-700"
                  title="Imprimir ou Salvar como PDF nativo do sistema"
                >
                  <Printer className="w-4 h-4 text-urb-primary" />
                  <span>Imprimir / Salvar PDF</span>
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleShare}
                  className="font-bold flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white border-slate-700"
                  title="Compartilhar comprovante no WhatsApp ou apps"
                >
                  <Share2 className="w-4 h-4 text-blue-400" />
                  <span>{copied ? "Texto Copiado!" : "Compartilhar"}</span>
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCopyCode}
                  className="font-bold flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white border-slate-700"
                  title="Copiar código de check-in"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-slate-300" />
                  )}
                  <span>{copied ? "Código Copiado!" : "Copiar Código"}</span>
                </Button>
              </div>
            ) : (
              <Button
                variant="secondary"
                size="md"
                onClick={() => setShowComprovante(true)}
                className="font-bold flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Ver Comprovante de Inscrição
              </Button>
            )}
          </div>

          {showComprovante && (
            <div
              ref={targetRef}
              className="comprovante-print-area p-6 sm:p-8 w-full max-w-lg bg-white text-slate-900 rounded-2xl shadow-2xl flex flex-col gap-4 border border-slate-200 mt-2"
            >
              <div className="flex justify-center">
                <img src={logo} alt="logo" className="w-20" />
              </div>
              <h2 className="text-center font-black text-lg tracking-tight uppercase m-0">
                COMPROVANTE DE INSCRIÇÃO
              </h2>
              <hr className="border-t border-slate-200 my-1" />

              <div className="flex flex-col gap-1.5 text-xs text-slate-700">
                <p className="m-0">
                  <strong>Atleta:</strong> {nomeAtleta}
                </p>
                {detail.is_terceiro && (
                  <p className="m-0">
                    <strong>Tipo:</strong> Inscrição para Terceiro
                    {detail.comprador_nome && ` (Comprador: ${detail.comprador_nome})`}
                  </p>
                )}
                <p className="m-0">
                  <strong>Prova:</strong> {detail.nome_corrida}
                </p>
                <p className="m-0">
                  <strong>Categoria:</strong> {detail.categoria}
                </p>
                <p className="m-0">
                  <strong>Faixa Etária:</strong> {detail.faixa_etaria}
                </p>
                {detail.tamanho_blusa && (
                  <p className="m-0">
                    <strong>Tamanho da Blusa:</strong> {detail.tamanho_blusa}
                  </p>
                )}
                <p className="m-0">
                  <strong>Nº de Peito:</strong> {detail.num_corredor || "-"}
                </p>
              </div>

              <div className="flex flex-col items-center justify-center text-center mt-4 pt-4 border-t border-slate-200">
                <p className="text-xs font-semibold text-slate-800 mb-2">
                  Apresente este código no Check-in:
                </p>

                <div className="p-2 border border-slate-200 rounded-xl bg-white shadow-sm">
                  <QRCodeSVG
                    value={detail.id_requisicao || detail.id?.toString() || ""}
                    size={140}
                    includeMargin={true}
                  />
                </div>

                <div className="text-[10px] text-slate-500 mt-3 flex flex-col gap-0.5">
                  <span className="flex items-center justify-center gap-1 font-mono">
                    Requisição: {detail.id_requisicao || "-"}
                  </span>
                  <span>ID Transação: {detail.uuid_transacao || "-"}</span>
                  <span>Método de Pagamento: {detail.metodo_pagamento || "-"}</span>
                  <span>Valor: R$ {detail.valor_pagamento || "-"}</span>
                  <span>
                    Data do Pagamento: {formatDate(detail.data_pagamento)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DetailInscricao;
